import axios from "axios";
import _ from "lodash";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Card } from "react-bootstrap";
import { Loader } from "../components/customComponents";
import { utilities } from "../components/utilities";
import { useSelector } from "react-redux";
import { useHistory, withRouter } from "react-router-dom";
import { useAppContext } from "../Office365/Office365Context";
import { getListFromOneDrive } from "../Office365/Office365Graph";
import { checkAccessList } from "../common/utilities";
import TrainingContent from "./TrainingContent";
import "./style.css";

import {
  MANDATORY_TRAINING,
  DEFAULTERS_LIST,
} from "../constants/urlConstants";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from "@material-ui/core";
import {
  prepareListUrl,
  TRAINING_PROJECT,
  TRAINING_PROJECT_ID,
} from "../constants/oneDriveConstants";

/* ----------------------------- Helper ----------------------------- */
const isTrainingVisible = (training, user) => {
  const matchAttr = (field) => {
    const values = training?.[field] || [];
    if (!Array.isArray(values)) return false;
    return values.includes("All") || values.includes(user?.[field]) || checkAccessList("all_trainings");
  };

  return (
    matchAttr("geography") &&
    matchAttr("team") &&
    matchAttr("competency") &&
    matchAttr("band")
  );
};

/* ----------------------------- Custom Hooks ----------------------------- */
const useMandatoryTrainings = (refresh) => {
  const [mandatory, setMandatory] = useState(null);

  useEffect(() => {
    const fetchMandatory = async () => {
      try {
        const response = await axios.get(MANDATORY_TRAINING);
        if (response.data.errorType !== "Error") {
          setMandatory(response.data);
        } else {
          console.error(response.data.errorMessage);
          setMandatory([]);
        }
      } catch (error) {
        utilities.showToast(error);
        setMandatory([]);
      }
    };
    fetchMandatory();
  }, [refresh]);

  return mandatory;
};

const useCompletionStatus = (employeeId, refresh) => {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await axios.get(
          `${DEFAULTERS_LIST}?pageSize=99&pageNumber=1&employee_id=${employeeId}`
        );
        if (response.status === 200 && response?.data?.results) {
          const formatted = {};
          for (const item of response.data.results) {
            const { training_name, completion_date, expiry_date } = item;
            formatted[training_name] = { completion_date, expiry_date };
          }
          setStatus(formatted);
        } else {
          setStatus({});
        }
      } catch (error) {
        console.error("Error fetching completion status", error);
        setStatus({});
      }
    };
    fetchStatus();
  }, [employeeId, refresh]);

  return status;
};

/* -------------------------- Reusable Accordion -------------------------- */
const TrainingListSection = ({
  title,
  items,
  expanded,
  onToggle,
  onSelect,
  filterValue,
}) => {
  const filteredItems = useMemo(() => {
    if (!filterValue || filterValue.length < 2) return items;
    return items.filter((item) =>
      item.name.toLowerCase().includes(filterValue.toLowerCase())
    );
  }, [items, filterValue]);

  return (
    <Accordion
      expanded={expanded}
      onChange={onToggle}
      style={{ boxShadow: "none", border: "none" }}
    >
      <AccordionSummary
        expandIcon={<i className="fa fa-angle-down" aria-hidden="true"></i>}
        style={{ borderBottom: "1px solid #ddd" }}
      >
        <div className="accordion-title-container">
          <Typography variant="h6">{title}</Typography>
          <div style={{ paddingLeft: ".5rem" }}> </div>
          <div className="circle-badge">
            <span className="badge-content">{items?.length ?? 0}</span>
          </div>
        </div>
      </AccordionSummary>
      <AccordionDetails>
        <div className="row" style={{ width: "100%" }}>
          {filteredItems?.map((item, index) => (
            <div key={index} className="col-md-3">
              <Card
                className="history-card"
                onClick={() => onSelect(item.name)}
              >
                <span>
                  {item.name}
                  {"@microsoft.graph.downloadUrl" in item ? (
                    <span className="pull-right">&darr;</span>
                  ) : null}
                </span>
              </Card>
            </div>
          ))}
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

/* ------------------------------ Main View ------------------------------- */
const TrainingHistory = (props) => {
  const [isLoading, setIsLoading] = useState(true); // global loader
  const [expanded, setExpanded] = useState({ mandatory: true, others: true });
  const [trainingData, setTrainingData] = useState(null);
  const [itemList, setItemList] = useState([]);
  const [filterValue, setFilterValue] = useState("");
  const [refresh, setRefresh] = useState(false);

  const searchRef = useRef(null);
  const user = useSelector(
    (state) => state.CurrentUserReducer.currentUserDetail
  );
  const history = useHistory();
  const app = useAppContext();

  const mandatory = useMandatoryTrainings(refresh);
  const trainingStatus = useCompletionStatus(user.employee_id, refresh);

  /* Derived training list */
  const trainingList = useMemo(() => {
    if (!mandatory || !props.oneDriveData) return null;
    return props.oneDriveData.reduce(
      (acc, item) => {
        const trainingMeta = props.readmeData.find(
          (t) => t.training_name === item.name
        );
        // 🚫 Skip if not visible for this user
        if (!trainingMeta || !isTrainingVisible(trainingMeta, user)) return acc;

        const isMandatory = mandatory.some(
          (t) =>
            t.training_name === item.name &&
            t.competency.includes(user.competency)
        );
        acc[isMandatory ? "mandatory" : "others"].push(item);
        return acc;
      },
      { mandatory: [], others: [] }
    );
  }, [mandatory, props.oneDriveData, user, props.readmeData]);

  /* Debounced search */
  const debounceSearch = useCallback(
    _.debounce((val) => setFilterValue(val), 1000),
    []
  );

  useEffect(() => {
    return () => debounceSearch.cancel();
  }, [debounceSearch]);

  /* Open training child item */
  const openChildItem = useCallback(
    async (childItemName) => {
      const trainingInfo =
        props.readmeData.find((t) => t.training_name === childItemName) || {
          training_name: childItemName,
        };

      // 🚫 Prevent opening invisible training
      if (!isTrainingVisible(trainingInfo, user)) {
        alert("🚫 You do not have access to this training.");
        return;
      }

      setTrainingData(trainingInfo);

      const childUrl = prepareListUrl(`${TRAINING_PROJECT}/${childItemName}`);
      const response = await getListFromOneDrive(app?.authProvider, childUrl);
      setItemList(response.value);
    },
    [props.readmeData, app?.authProvider, user]
  );

  /* Global spinner control */
  useEffect(() => {
    if (mandatory !== null && trainingStatus !== null && props.oneDriveData) {
      setIsLoading(false);
    }
  }, [mandatory, trainingStatus, props.oneDriveData]);

  /* URL params handling */
  useEffect(() => {
    const params = props.match?.params[0];
    const path = params?.split("/")[2];

    if (path && props.readmeData?.length && user) {
      setIsLoading(true);
      openChildItem(path).finally(() => setIsLoading(false));
    } else {
      setTrainingData(null);
    }
  }, [props.match.params, openChildItem, props.readmeData, user]);


  /* Helper for toggling accordion */
  const toggleAccordion = (key) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  /* Helper for loading wrapper */
  const withLoading = async (fn) => {
    setIsLoading(true);
    await fn();
    setIsLoading(false);
  };

  if (isLoading) return <Loader />;

  return (
    <div className="container">
      {trainingData ? (
        <>
          <div className="col-md-1">
            <div
              style={{ padding: "15% 0" }}
              onClick={() => {
                history.push("/trainings/completed/" + TRAINING_PROJECT_ID);
                setTrainingData(null);
                setFilterValue("");
              }}
            >
              <i
                className="fa fa-arrow-circle-left fa-2x"
                style={{ color: "#5776E7", cursor: "pointer" }}
              />
            </div>
          </div>
          <TrainingContent
            trainingData={trainingData}
            trainingStatus={trainingStatus?.[trainingData.training_name]}
            fileLinks={itemList}
            user={user}
            refresh={refresh}
            setRefresh={setRefresh}
          />
        </>
      ) : (
        <>
          <div className="filterContainer">
            <div className="form-group has-search">
              <span className="fa fa-search form-control-feedback"></span>
              <input
                type="text"
                id="header-search"
                className="form-control"
                placeholder="Search..."
                ref={searchRef}
                onChange={(e) => debounceSearch(e.target.value)}
              />
            </div>
          </div>

          <TrainingListSection
            title="Mandatory"
            items={trainingList?.mandatory || []}
            expanded={expanded.mandatory}
            onToggle={() => toggleAccordion("mandatory")}
            onSelect={(name) =>
              withLoading(() =>
                openChildItem(name).then(() =>
                  props.history.push(props.match.url + "/" + name)
                )
              )
            }
            filterValue={filterValue}
          />

          <TrainingListSection
            title="Others"
            items={trainingList?.others || []}
            expanded={expanded.others}
            onToggle={() => toggleAccordion("others")}
            onSelect={(name) =>
              withLoading(() =>
                openChildItem(name).then(() =>
                  props.history.push(props.match.url + "/" + name)
                )
              )
            }
            filterValue={filterValue}
          />
        </>
      )}
    </div>
  );
};

export default withRouter(TrainingHistory);
